// Utility to check if a tool or form is complete for MCP tools
import { McpTool, ToolDisplayStatus } from '../types/types';

// For react-hook-form: pass a watch function
export function isToolFormComplete(watch: (field: string) => any): boolean {
  const base = !!watch('name') && !!watch('description') &&
    !!watch('httpMethod') && !!watch('inputSchema');
  if (!base) return false;

  // SELECT (MSSQL) tools use a SQL bodyTemplate instead of a REST path/queryMap.
  if (watch('httpMethod') === 'SELECT') return !!watch('bodyTemplate');
  return !!watch('queryMap') && !!watch('path');
}

// For a McpTool object. Pass isMssql (connector.connectionTypeId === 7) - MSSQL tools
// use a SQL bodyTemplate instead of a REST path/queryMap, and their httpMethod is not
// reliably 'SELECT' (imported/legacy tools can carry 'GET').
export function isToolComplete(tool: McpTool, isMssql = false): boolean {
  const base = !!tool.name && !!tool.description &&
    !!tool.httpMethod && !!tool.inputSchema;
  if (!base) return false;

  if (isMssql || tool.httpMethod === 'SELECT') return !!tool.bodyTemplate;
  return !!tool.queryMap && !!tool.path;
}

export function getToolDisplayStatus(tool: McpTool, isConnectorActive: boolean): ToolDisplayStatus {
  if (!isToolComplete(tool)) return 'in_progress';
  if (getToolValidationError(tool)) return 'missing';
  if (!isConnectorActive || !tool.enabled) return 'disabled';
  return 'enabled';
}

/**
 * Extract all {{var}} template variables from a string.
 */
function extractTemplateVars(text: string): string[] {
  if (!text) return [];
  const vars = new Set<string>();
  for (const m of text.matchAll(/\{\{\s*(\w+)\s*\}\}/g)) {
    vars.add(m[1]);
  }
  return Array.from(vars);
}

/**
 * Validate a tool for MCP readiness. Returns an error message if invalid, or null if valid.
 * Checks that all template variables in path, query params, and body template
 * have corresponding input fields defined in inputSchema.
 */
export function getToolValidationError(tool: McpTool, isMssql = false): string | null {
  if (!isToolComplete(tool, isMssql)) return null; // incomplete tools are handled separately

  const schema = tool.inputSchema as Record<string, any> | undefined;
  const fieldNames = new Set<string>(
    schema?.properties ? Object.keys(schema.properties) : []
  );

  // Collect all template variables from path
  const pathOnly = (tool.path || '').split('?')[0];
  const pathVars = extractTemplateVars(pathOnly);

  // Collect from dynamic query params
  const queryMap = tool.queryMap as Array<{ key: string; value: string; dynamic: boolean }> | undefined;
  const queryVars: string[] = [];
  if (Array.isArray(queryMap)) {
    for (const q of queryMap) {
      if (q.dynamic) queryVars.push(...extractTemplateVars(q.value || ''));
    }
  }

  // Collect from body template (POST/PUT/PATCH, or the SQL query for MSSQL tools)
  const method = (tool.httpMethod || 'GET').toUpperCase();
  const bodyVars = (isMssql || method !== 'GET') ? extractTemplateVars(tool.bodyTemplate || '') : [];

  const allVars = [...new Set([...pathVars, ...queryVars, ...bodyVars])];
  const missing = allVars.filter(v => !fieldNames.has(v));

  if (missing.length > 0) {
    return `Missing input field${missing.length > 1 ? 's' : ''}: ${missing.join(', ')}`;
  }

  return null;
}